/**
 * useManualWarehouses — the warehouses we run ourselves.
 *
 * Two registry facts have to agree: the site is one of ours
 * (`warehouse_type = 'own_3pl'`) and nothing external receives for it
 * (`warehouse.integration.provider = 'manual'`). OPLOG and Argents are
 * own_3pl too, so the type alone would list them.
 *
 * This is what drives the Warehousing section's own-warehouse tabs and its
 * sidebar entries, so opening a new site is a registry row rather than an edit
 * to a hardcoded tab list. Brixton and Milton Keynes appear through exactly
 * the same path, and neither is named anywhere in the frontend.
 */
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '../AuthContext';
import { useApi } from '../api';
import { useWarehouses } from './useWarehouses';
import type { WarehouseInfo } from './useWarehouses';
import { MANUAL_PROVIDER, fetchWarehouseProvider } from './warehouseIntegrations';

export interface UseManualWarehousesResult {
  warehouses: WarehouseInfo[];
  loading: boolean;
  error: string | null;
}

export function useManualWarehouses(): UseManualWarehousesResult {
  const { token, logout } = useAuth();
  const { apiFetch } = useApi(token, logout);
  const {
    warehouses: ownWarehouses,
    loading: listLoading,
    error: listError,
  } = useWarehouses({ warehouseType: 'own_3pl' });

  const [manualCodes, setManualCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const codes = useMemo(
    () => ownWarehouses.map((w) => w.code).join(','),
    [ownWarehouses],
  );

  useEffect(() => {
    if (listLoading) return;
    const list = codes ? codes.split(',') : [];
    if (list.length === 0) {
      setManualCodes([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all(
      list.map(async (code) => {
        try {
          return (await fetchWarehouseProvider(apiFetch, code)) === MANUAL_PROVIDER
            ? code
            : null;
        } catch {
          // One unreadable integration must not blank the whole section: the
          // site is simply left out until its row can be read.
          return null;
        }
      }),
    )
      .then((resolved) => {
        if (cancelled) return;
        setManualCodes(resolved.filter((code): code is string => code != null));
        setError(null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError((err as Error)?.message || 'Could not read warehouse integrations');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiFetch, codes, listLoading]);

  const warehouses = useMemo(
    () => ownWarehouses.filter((w) => manualCodes.includes(w.code)),
    [manualCodes, ownWarehouses],
  );

  return { warehouses, loading: listLoading || loading, error: listError || error };
}

export default useManualWarehouses;
